import type React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Toolbar,
  FormGroup,
  Card,
  CardContent,
} from "@mui/material";
import { Print, Person, Business, Event, MusicNote } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import pattern from "../../../assets/pattern.png";
import logoBrown from "../../../assets/logoBrown.png";
import Muzukuruimg3 from "../../../assets/Muzukuruimg3.png";
import apiService from "../../../service/Api";
import { toast } from "react-toastify";
import CustomTextField from "../../../components/common/Textfield";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import Button from "../../../components/common/Button";
import { BookingResponse } from "../types/view-booking";
import { IFormData } from "./types/church-booking-form";

const ChurchBookingForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const bookingId = id || location.state?.bookingId;

  const [formData, setFormData] = useState<IFormData>({
    deceased_name: "",
    deceased_surname: "",
    deceased_christian_name: "",
    date_of_service: "",
    venue: "",
    is_member: false,
    client_name: "",
    client_surname: "",
    email: "",
    phone: "",
    relationship_to_deceased: "",
    client_christian_name: "",
    organist_name: "",
    organist_cell: "",
    pastor_name: "",
    pastor_cell: "",
    tea_area: "",
    number_of_people: "",
    tea_arrangement: "",
    hpc_tea_ladies: false,
    flower_arrangement: "",
    florist_contact: "",
    hymns: ["", "", "", "", ""],
    music: ["", "", "", ""],
    sound_requirements: ["", ""],
    special_requests: ["", "", ""],
    signature_name: "",
    signature: "",
    donation: "",
    date_paid: "",
  });

  const [bookingData, setBookingData] = useState<BookingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = createTheme({
    palette: {
      primary: {
        main: reduxTheme.primary_colors?.[400] || "#614438",
      },
      secondary: {
        main: reduxTheme.secondary_colors?.[400] || "#8B4513",
      },
    },
  });

  // Fetch booking data
  useEffect(() => {
    const fetchBookingData = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const bookingResponse = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${bookingId}/`
        );
        const booking = bookingResponse.data;

        setBookingData(booking);

        // Map API data to form fields
        const mappedFormData: IFormData = {
          deceased_name: booking.deceased?.[0]?.first_name || "",
          deceased_surname: booking.deceased?.[0]?.surname || "",
          deceased_christian_name:
            booking.deceased?.[0]?.nickname ||
            booking.deceased?.[0]?.second_name ||
            "",
          date_of_service: booking.services?.[0]?.date_of_service
            ? new Date(booking.services[0].date_of_service).toLocaleDateString()
            : "",
          venue: booking.services?.[0]?.venue
            ? `${booking.services[0].venue.name || ""}`
                .trim()
                .replace(/\s+/g, " ")
            : booking.services?.[0]?.service_address || "",
          is_member: false,
          client_name: booking.contact?.first_name || "",
          client_surname: booking.contact?.last_name || "",
          email: booking.contact?.email || "",
          phone: booking.contact?.phone || "",
          relationship_to_deceased:
            booking.deceased?.[0]?.relationship_to_deceased || "",
          client_christian_name: booking.contact?.address_one || "",
          organist_name: "",
          organist_cell: "",
          pastor_name: "",
          pastor_cell: "",
          tea_area: "",
          number_of_people: "",
          tea_arrangement: "",
          hpc_tea_ladies: false,
          flower_arrangement: "",
          florist_contact: "",
          hymns: ["", "", "", "", ""],
          music: ["", "", "", ""],
          sound_requirements: ["", ""],
          special_requests: ["", "", ""],
          signature_name: "",
          signature: "",
          donation: "",
          date_paid: "",
        };
        setFormData(mappedFormData);
        setError(null);
      } catch (err: any) {
        let errorMessage = "Failed to load booking data. Please try again.";
        if (err.response?.status === 500) {
          errorMessage =
            "Server error: Unable to retrieve data. Please try again later or contact support.";
        } else if (err.response?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (err.response?.status === 403) {
          errorMessage =
            "Permission denied: You do not have access to this booking.";
          navigate("/bookings");
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }

        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId, navigate]);

  const handleInputChange = (
    field: keyof IFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayInputChange = (
    field: keyof IFormData,
    index: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (prev[field] as string[]).map((item, i) =>
        i === index ? value : item
      ),
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Header Bar */}

      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1 }}></Typography>

        <Button
          startIcon={<Print />}
          onClick={handlePrint}
          className="print-button"
        >
          Print
        </Button>
      </Toolbar>

      {/* Main Content */}
      <Box
        className="print-container"
        sx={{
          backgroundImage: `url(${pattern})`,
          backgroundSize: "100px 100px",
          minHeight: "100vh",
          minWidth: "10vh",
        }}
      >
        {/* Header Logo for Print */}
        <Box className="header-logo" sx={{ display: "none" }}>
          <img
            alt="Muzukuru Header Logo"
            width="300px"
            height="70px"
            src={logoBrown || "/placeholder.svg"}
            style={{ objectFit: "contain" }}
          />
        </Box>

        {/* Logo Container */}
        <Box
          className="logo-container logo"
          display="flex"
          justifyContent="center"
          mb={1}
        >
          <img
            alt="Church Logo"
            width="250px"
            height="80px"
            src={logoBrown || "/placeholder.svg"}
            style={{ objectFit: "contain" }}
          />
        </Box>

        <Box maxWidth="1000px" mx="auto">
          <Paper
            className="form-container"
            elevation={3}
            sx={{ mb: 3, overflow: "hidden" }}
          >
            {/* Header Bar */}
            <Box
              sx={{
                bgcolor: reduxTheme.primary_colors?.[400] || "#614438",
                color: reduxTheme.primary_colors?.[900] || "#ffffff",
                p: 0,
                backgroundImage: "url(/static/images/pattern.png)",
                backgroundSize: "20px 20px",
                borderBottom: "3px solid black",
                position: "relative",
                height: "10px",
              }}
            />

            <Box p={3}>
              <Typography
                variant="h4"
                align="center"
                sx={{ color: reduxTheme.primary_colors[400] }}
                fontWeight="bold"
                mb={3}
              >
                Church Internal Booking Form
              </Typography>

              {/* Deceased Details */}
              <Card
                className="print-tight-card deceased-card"
                sx={{
                  mb: 3,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: reduxTheme.primary_colors[400],
                    }}
                  >
                    <Person /> Deceased Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Name"
                        value={formData.deceased_name}
                        onChange={(e) =>
                          handleInputChange("deceased_name", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>

                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Surname"
                        value={formData.deceased_surname}
                        onChange={(e) =>
                          handleInputChange("deceased_surname", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Date Of Service"
                        value={formData.date_of_service}
                        onChange={(e) =>
                          handleInputChange("date_of_service", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Venue"
                        value={formData.venue}
                        onChange={(e) =>
                          handleInputChange("venue", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Christian Name"
                        onChange={(e) =>
                          handleInputChange(
                            "deceased_christian_name",
                            e.target.value
                          )
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <FormGroup row>
                        <Typography
                          variant="body1"
                          sx={{ mr: 2, alignSelf: "center" }}
                        >
                          Member:
                        </Typography>
                        <FormControlLabel control={<Checkbox />} label="YES" />
                        <FormControlLabel control={<Checkbox />} label="NO" />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Client Details */}
              <Card
                className="print-tight-card client-card"
                sx={{
                  mb: 3,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: reduxTheme.primary_colors[400],
                    }}
                  >
                    <Business /> Client Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Client Name"
                        value={formData.client_name}
                        onChange={(e) =>
                          handleInputChange("client_name", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Surname"
                        value={formData.client_surname}
                        onChange={(e) =>
                          handleInputChange("client_surname", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Cell No"
                        value={formData.phone}
                        onChange={(e) =>
                          handleInputChange("phone", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Relationship"
                        value={formData.relationship_to_deceased}
                        onChange={(e) =>
                          handleInputChange(
                            "relationship_to_deceased",
                            e.target.value
                          )
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Address"
                        value={formData.client_christian_name}
                        onChange={(e) =>
                          handleInputChange(
                            "client_christian_name",
                            e.target.value
                          )
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Funeral Details */}
              <Card
                className="print-tight-card funeral-card"
                sx={{
                  mb: 3,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: reduxTheme.primary_colors[400],
                    }}
                  >
                    <Event /> Funeral Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Name of Pastor"
                        value={formData.pastor_name}
                        onChange={(e) =>
                          handleInputChange("pastor_name", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Cell No"
                        value={formData.pastor_cell}
                        onChange={(e) =>
                          handleInputChange("pastor_cell", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Name of Organist/Pianist"
                        value={formData.organist_name}
                        onChange={(e) =>
                          handleInputChange("organist_name", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Cell No"
                        value={formData.organist_cell}
                        onChange={(e) =>
                          handleInputChange("organist_cell", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Area to be used for Teas"
                        value={formData.tea_area}
                        onChange={(e) =>
                          handleInputChange("tea_area", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="No of people"
                        value={formData.number_of_people}
                        onChange={(e) =>
                          handleInputChange("number_of_people", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <CustomTextField
                        fullWidth
                        label="Arrangement for TEAS"
                        value={formData.tea_arrangement}
                        onChange={(e) =>
                          handleInputChange("tea_arrangement", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6 }} sx={{ mb: 2 }}>
                      <FormGroup row>
                        <Typography
                          variant="body2"
                          sx={{ mr: 2, alignSelf: "center" }}
                        >
                          HPC TEA LADIES NEEDED:
                        </Typography>
                        <FormControlLabel control={<Checkbox />} label="YES" />
                        <FormControlLabel control={<Checkbox />} label="NO" />
                      </FormGroup>
                    </Grid>
                  </Grid>

                  <Typography variant="body2" fontWeight="bold" mb={1}>
                    FLOWER ARRANGEMENTS
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: reduxTheme.primary_colors[700] }}
                    mb={2}
                  >
                    NB: All flowers must be arranged and placed day OR morning
                    before the service.
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <FormControlLabel control={<Checkbox />} label="SELF" />
                    <FormControlLabel control={<Checkbox />} label="FLORIST" />

                    <Grid size={{ xs: 6, md: 8 }}>
                      <CustomTextField
                        fullWidth
                        label="Florist Contact Details"
                        value={formData.florist_contact}
                        onChange={(e) =>
                          handleInputChange("florist_contact", e.target.value)
                        }
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card
                className="order-of-service-card , funeral-details-break"
                sx={{
                  mb: 3,
                  border: "2px solid",
                  borderColor: "primary.main",
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: reduxTheme.primary_colors[400],
                    }}
                  >
                    <MusicNote /> Order of Service
                  </Typography>

                  <Grid container spacing={3}>
                    {/* HYMNS */}
                    <Grid size={{ xs: 6, md: 6 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        HYMNS
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: reduxTheme.primary_colors[700] }}
                        mb={2}
                      >
                        Please note that the hymns/songs required must be
                        clearly typed on the order of service or the hymn books
                        in the church can be used.
                      </Typography>
                      {formData.hymns.map((hymn, index) => (
                        <CustomTextField
                          key={index}
                          fullWidth
                          label={`${index + 1}.`}
                          value={hymn}
                          onChange={(e) =>
                            handleArrayInputChange(
                              "hymns",
                              index,
                              e.target.value
                            )
                          }
                          variant="standard"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Grid>

                    {/* MUSIC */}
                    <Grid size={{ xs: 6, md: 6 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        MUSIC
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: reduxTheme.primary_colors[700] }}
                        mb={2}
                      >
                        Should you require music - other than that of the
                        organist/pianist to be played - this must be supplied on
                        a "stick" and handed to the office 12 hrs before the
                        service.
                      </Typography>
                      {formData.music.map((music, index) => (
                        <CustomTextField
                          key={index}
                          fullWidth
                          label={`${index + 1}.`}
                          value={music}
                          onChange={(e) =>
                            handleArrayInputChange(
                              "music",
                              index,
                              e.target.value
                            )
                          }
                          variant="standard"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Grid>
                  </Grid>

                  <Box mt={3}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      SOUND REQUIREMENT
                    </Typography>
                    {formData.sound_requirements.map((requirement, index) => (
                      <CustomTextField
                        key={index}
                        fullWidth
                        label={`${index + 1}.`}
                        value={requirement}
                        onChange={(e) =>
                          handleArrayInputChange(
                            "sound_requirements",
                            index,
                            e.target.value
                          )
                        }
                        variant="standard"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Box>

                  <Box mt={3}>
                    <Typography variant="body2" fontWeight="bold" mb={1}>
                      SPECIAL REQUESTS
                    </Typography>
                    {formData.special_requests.map((request, index) => (
                      <CustomTextField
                        key={index}
                        fullWidth
                        label={`${index + 1}.`}
                        value={request}
                        onChange={(e) =>
                          handleArrayInputChange(
                            "special_requests",
                            index,
                            e.target.value
                          )
                        }
                        variant="standard"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* Final Details */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="NAME"
                    onChange={(e) =>
                      handleInputChange("signature_name", e.target.value)
                    }
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="SIGNATURE"
                    value={formData.signature}
                    onChange={(e) =>
                      handleInputChange("signature", e.target.value)
                    }
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="DONATION"
                    // value={formData.donation}
                    onChange={(e) =>
                      handleInputChange("donation", e.target.value)
                    }
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 6 }}>
                  <CustomTextField
                    fullWidth
                    label="DATE PAID"
                    value={formData.date_paid}
                    onChange={(e) =>
                      handleInputChange("date_paid", e.target.value)
                    }
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Box
            className="footer-container logo"
            display="flex"
            justifyContent="center"
            mt={3}
          >
            <img
              alt="Church Footer Logo"
              width="180px"
              height="80px"
              src={Muzukuruimg3 || "/placeholder.svg"}
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ChurchBookingForm;
